/* eslint-disable sort-exports/sort-exports */
import { z } from "zod";
import { atom } from "jotai";

import { JotaiTool } from "../../../index";
import type { ChoiceOption } from "../shared/types";

export const GetAppChoicesParams = z.object({
  implementation_id: z
    .string()
    .describe(
      "The app implementation ID with version (e.g., 'GoogleSheetsV2CLIAPI@2.8.0') from get_app_auth",
    ),
  action_key: z
    .string()
    .describe(
      "The specific action key containing the field that needs choices",
    ),
  field_key: z
    .string()
    .describe("The specific field key that needs dropdown choices"),
  displayMessage: z
    .string()
    .optional()
    .describe(
      "Short 3-5 word friendly message describing what you're doing, like 'Loading field options...' or 'Getting dropdown choices...'",
    ),
});

export type GetAppChoicesParamsType = z.infer<typeof GetAppChoicesParams>;

export const getAppChoicesExecute = async (rawArgs: any): Promise<any> => {
  let params: GetAppChoicesParamsType;
  try {
    params = GetAppChoicesParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[GET_APP_CHOICES] Error parsing arguments:", error);
    return {
      $error: {
        message: `Error: Invalid arguments provided for get_app_choices. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
      },
    };
  }

  const { implementation_id, action_key, field_key } = params;

  try {
    // CRITICAL: Block field mapping if authentication not verified
    // This tool should only be called AFTER get_app_auth has confirmed authentication
    const authCheckUrl = `/api/canvasui/composer/getAppAuth?implementation_id=${encodeURIComponent(implementation_id)}`;

    const authCheckResponse = await fetch(authCheckUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (authCheckResponse.ok) {
      const authData = await authCheckResponse.json();
      if (authData.needs_new_auth || authData.authentications?.length === 0) {
        return {
          $error: {
            message: `BLOCKED: Authentication must be verified before retrieving field choices. Run get_app_auth first to verify authentication for ${implementation_id.split("@")[0]}.`,
          },
        };
      }
    }

    // Validate implementation_id format
    if (!/^[A-Za-z0-9]+@\d+\.\d+\.\d+$/.test(implementation_id)) {
      return {
        $error: {
          message: `Invalid implementation_id format: ${implementation_id}. Expected format: AppName@version (e.g., GmailCLIAPI@1.5.0)`,
        },
      };
    }

    // Call our backend caching route
    const searchParams = new URLSearchParams({
      implementation_id,
      action_key,
      field_key,
    });
    const apiUrl = `/api/canvasui/composer/getAppChoices?${searchParams.toString()}`;

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include", // Include cookies for authentication
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = {
          error: `API returned ${response.status}: ${response.statusText}`,
        };
      }

      console.error(
        `[GET_APP_CHOICES] API error: ${response.status}`,
        errorData,
      );

      return {
        $error: {
          message: `Failed to fetch field choices: ${errorData.error || response.statusText}`,
        },
      };
    }

    const data = await response.json();

    if (!data.success) {
      return {
        $error: {
          message: `Invalid response format from getAppChoices API`,
        },
      };
    }

    const choices: ChoiceOption[] = data.choices || [];

    // If no choices available, still return success but indicate field doesn't support choices
    if (choices.length === 0) {
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(
              {
                implementation_id,
                action_key,
                field_key,
                app_name: implementation_id.split("@")[0],
                choices: [],
                has_more: false,
                message:
                  "This field does not have predefined choices (free-form input field)",
                cache_info: data.cache_info || "No choices to cache",
              },
              null,
              2,
            ),
          },
        ],
      };
    }

    // Group choices by type if they have samples
    const choicesWithSamples = choices.filter((choice) => choice.sample);
    const choicesWithoutSamples = choices.filter((choice) => !choice.sample);

    return {
      content: [
        {
          type: "text",
          text: JSON.stringify(
            {
              implementation_id,
              action_key,
              field_key,
              app_name: implementation_id.split("@")[0],
              choices: choices,
              has_more: data.has_more || false,
              choice_count: choices.length,
              cache_info: data.cache_info || "Retrieved from cache",
              summary: {
                total_choices: choices.length,
                choices_with_samples: choicesWithSamples.length,
                choices_without_samples: choicesWithoutSamples.length,
                has_pagination: data.has_more || false,
              },
              // Include first few choices as examples
              examples: choices.slice(0, 5).map((choice) => ({
                label: choice.label,
                value: choice.value,
                has_sample: !!choice.sample,
              })),
            },
            null,
            2,
          ),
        },
      ],
    };
  } catch (error) {
    console.error("[GET_APP_CHOICES] Error in execution:", error);
    return {
      $error: {
        message: `Error fetching field choices: ${error instanceof Error ? error.message : String(error)}`,
      },
    };
  }
};

export const getAppChoicesDescription = `Fetches dropdown choice options for a specific field in a Zapier app action with caching.

**CRITICAL**: This tool BLOCKS execution if authentication is not verified. It automatically
checks authentication status and will return an error if get_app_auth has not been called
and confirmed first.

This tool retrieves the available options for dropdown fields, select boxes, and other choice-based
form fields in app actions.

**MANDATORY USAGE**: Only call after get_app_auth has verified and confirmed authentication.

Parameters:
- implementation_id: Full app identifier with version from get_app_auth (e.g., "GmailCLIAPI@1.5.0")
- action_key: The specific action containing the field
- field_key: The specific field that needs dropdown options

Returns:
- Complete list of available choices with labels and values
- Sample data for choices (if available)
- Pagination information (has_more flag)
- Caching information (60-minute cache)
- Summary statistics and examples

The results are cached for 60 minutes to improve performance. Authentication is verified
automatically before retrieving choices.

This is the final tool in the field mapping hierarchy and enforces the mandatory sequence:
get_app_needs → get_app_auth → get_app_choices.

**ERROR**: If called without authentication verification, returns "BLOCKED" error message.`;

export const getAppChoicesAtom = atom<
  JotaiTool<z.infer<typeof GetAppChoicesParams>>
>(() => ({
  name: "get_app_choices",
  description: getAppChoicesDescription,
  parameters: GetAppChoicesParams,
  execute: atom(
    null, // read function (not used for write-only atom)
    async (_get, _set, { args }) => {
      return getAppChoicesExecute(args);
    },
  ),
}));

getAppChoicesAtom.debugLabel = "getAppChoicesAtom";

/* eslint-enable sort-exports/sort-exports */
